import _, { keyBy } from "lodash";
import { v4 as uuidv4 } from "uuid";

/**
 * Sync row or column headers after modifying calibration. Mutates newCalibration parameter.
 * @param {Array} oldCalibration - Existing table axis calibration
 * @param {Array} newCalibration - New table axis calibration
 */
function syncHeaders(oldCalibration, newCalibration) {
	const emptyHeaderValue = null;

	// process row headers
	if (oldCalibration?.rows.headers.length) {
		// number of existing row headers is different than the new number of rows
		if (oldCalibration.rows.headers.length < newCalibration.rows.num) {
			// fill in the rest with nulls
			newCalibration.rows.headers.length = newCalibration.rows.num;
			newCalibration.rows.headers.fill(
				emptyHeaderValue,
				oldCalibration.rows.headers.length,
			);
		} else if (oldCalibration.rows.headers.length > newCalibration.rows.num) {
			// discard the extra headers
			newCalibration.rows.headers.splice(newCalibration.rows.num);
		}
		// do nothing if headers are equal
	} else {
		// no existing row headers, fill in row headers with nulls
		newCalibration.rows.headers.length = newCalibration.rows.num;
		newCalibration.rows.headers.fill(emptyHeaderValue, 0);
	}

	// process column headers
	if (oldCalibration?.cols.headers.length) {
		// number of existing column headers is different than the new number of columns
		if (oldCalibration.cols.headers.length < newCalibration.cols.num) {
			// fill in the rest with nulls
			newCalibration.cols.headers.length = newCalibration.cols.num;
			newCalibration.cols.headers.fill(
				emptyHeaderValue,
				oldCalibration.cols.headers.length,
			);
		} else if (oldCalibration.cols.headers.length > newCalibration.cols.num) {
			// discard the extra headers
			newCalibration.cols.headers.splice(newCalibration.cols.num);
		}
		// do nothing if headers are equal
	} else {
		// no existing column headers, fill in column headers with nulls
		newCalibration.cols.headers.length = newCalibration.cols.num;
		newCalibration.cols.headers.fill(emptyHeaderValue, 0);
	}
}

// headerless flags set by each header type select value
const HEADER_TYPE_HEADERLESS = {
	both: { rows: false, cols: false },
	rows: { rows: false, cols: true },
	cols: { rows: true, cols: false },
};

/**
 * Header type select value for a table calibration's headerless flags.
 * @param {Object} calibration - Table axis calibration
 * @returns {String} "both", "rows" (row headers only) or "cols" (column headers only)
 */
function headerTypeValue(calibration) {
	if (calibration.rows.headerless) {
		return "cols";
	}
	if (calibration.cols.headerless) {
		return "rows";
	}
	return "both";
}

/**
 * Header types a completed table can switch to from the edit sidebar. A header
 * direction can only be switched while it holds no header data: switching one
 * off only flips its headerless flag, leaving the header tags already copied
 * into its cells behind, and switching one on would revive headers its cells
 * never received. The case this enables is adding an empty header to a table
 * calibrated without one, e.g. a column header on a "Row headers only" table.
 * @param {Object} calibration - Table axis calibration
 * @returns {Object} Whether each header type select value can be selected
 */
function allowedHeaderTypes(calibration) {
	const hasHeaderData = (direction) =>
		calibration[direction].headers.some((header) => !!header);

	return _.mapValues(HEADER_TYPE_HEADERLESS, (headerless) =>
		["rows", "cols"].every(
			(direction) =>
				headerless[direction] === !!calibration[direction].headerless ||
				!hasHeaderData(direction),
		),
	);
}

/**
 * Injects function to set up table axis and start alignment.
 * @param {Object} ids - IDs of table axis related elements
 */
function apply(wpd, wpdDocument, dataEntryVM, ids) {
	const calibration = {
		area: [],
		rotation: 0,
		schema: "",
		rows: {
			num: 1,
			dividers: [],
			headers: [],
			headerless: false,
		},
		cols: {
			num: 1,
			dividers: [],
			headers: [],
			headerless: false,
		},
	};

	let existingArea = [];

	/**
	 * Resets calibration object.
	 */
	const resetCalibration = () => {
		calibration.area.length = 0;
		calibration.rows.num = 1;
		calibration.rows.dividers.length = 0;
		calibration.rows.headers.length = 0;
		calibration.rows.headerless = false;
		calibration.cols.num = 1;
		calibration.cols.dividers.length = 0;
		calibration.cols.headers.length = 0;
		calibration.cols.headerless = false;
		calibration.rotation = 0;
		calibration.schema = "";
		calibration.cellTagGroup = null;
		calibration.cellTagGroups = [];
		calibration.calibrationComplete = false;
	};

	wpd.alignAxes.resetTableCalibration = () => {
		resetCalibration();
	};

	/**
	 * Table axis alignment entry point from table plot info dialog.
	 */
	wpd.alignAxes.pickArea = (back) => {
		// store existing area
		existingArea = [...calibration.area];

		if (!back) {
			// close axis calibration info popup
			wpd.popup.close(ids.infoPopup);

			// clear local calibration
			resetCalibration();
		}

		const axis = wpd.tree.getActiveAxes();
		if (axis) {
			const metadata = axis.getMetadata();
			const calibration = metadata.calibration;
			calibration.wizardStep = "defineTableArea";
			calibration.rotation = wpd.graphicsWidget.getRotation();
			metadata.calibration = calibration;
			axis.setMetadata(metadata);
		}

		// trigger table axis area alignment
		wpd.graphicsWidget.setTool(new wpd.AlignmentAreaTool(calibration));

		// display table axis area sidebar
		wpd.sidebar.show(ids.areaSidebar);
	};

	/**
	 * On change handler for table axis numbers of rows and columns inputs.
	 */
	wpd.alignAxes.updateRowsAndColumns = (el) => {
		const id = el.getAttribute("id");
		const value = parseInt(el.value, 10);

		// set desired number of rows or columns
		if (id === ids.rowInput) {
			calibration.rows.num = value;
		} else if (id === ids.colInput) {
			calibration.cols.num = value;
		}

		// update dividers in the alignment rows and columns tool
		wpd.events.dispatch("wpd.tableAxis.updateDividers");

		// force redraw
		wpd.graphicsWidget.forceHandlerRepaint();
	};

	/**
	 * Triggers table axis recalibration from custom tweak calibration button.
	 */
	wpd.alignAxes.tweakTableCalibration = () => {
		// clear local calibration
		resetCalibration();

		// get calibration data from existing table axis
		const axis = wpd.tree.getActiveAxes();
		const metadata = axis.getMetadata();

		// set local calibration to existing table axis data
		calibration.area = metadata.calibration.area;
		calibration.rows = metadata.calibration.rows;
		calibration.cols = metadata.calibration.cols;
		calibration.rotation = metadata.calibration.rotation;

		calibration.cellTagGroup = metadata.calibration.cellTagGroup;
		calibration.cellTagGroups = metadata.calibration.cellTagGroups;
		calibration.calibrationComplete = metadata.calibration.calibrationComplete;
		calibration.schema = metadata.calibration.schema;
		calibration.wizardStep = metadata.calibration.wizardStep;

		// store existing area
		existingArea = [...calibration.area];

		// trigger table axis area alignment
		wpd.graphicsWidget.setTool(new wpd.AlignmentAreaTool(calibration));

		// display table axis area sidebar
		wpd.sidebar.show(ids.areaSidebar);
	};

	wpd.alignAxes.backToRowsAndColumns = () => {
		const axis = wpd.tree.getActiveAxes();
		if (!axis) {
			return;
		}

		wpd.alignAxes.tweakTableCalibration();
		wpd.alignAxes.defineRowsAndColumns();
	};

	/**
	 * Triggers table axis rows and columns calibration after table area definition.
	 */
	wpd.alignAxes.defineRowsAndColumns = (addAxis = false) => {
		if (
			_.isEqual(existingArea, calibration.area) &&
			(calibration.rows.num > 1 || calibration.cols.num > 1)
		) {
			// load existing rows and columns if either exists and area has not changed
			wpdDocument.getElementById(ids.rowInput).value = calibration.rows.num;
			wpdDocument.getElementById(ids.colInput).value = calibration.cols.num;
		} else {
			// otherwise revert to default
			wpdDocument.getElementById(ids.rowInput).value = 1;
			calibration.rows.num = 1;
			calibration.rows.dividers.length = 0;

			wpdDocument.getElementById(ids.colInput).value = 1;
			calibration.cols.num = 1;
			calibration.cols.dividers.length = 0;
		}

		calibration.rotation = wpd.graphicsWidget.getRotation();
		const existingAxis = wpd.tree.getActiveAxes();

		const tableAxis = existingAxis ?? new wpd.ImageAxes();
		if (!existingAxis) {
			tableAxis.name = wpd.custom.makeAxisName("Table");
		}

		wpd.tree.setActiveAxis(tableAxis);

		const metadata = tableAxis.getMetadata();

		metadata.table = true;
		calibration.wizardStep = "defineRowsAndColumns";
		metadata.calibration = calibration;

		tableAxis.setMetadata(metadata);

		if (addAxis) {
			const plotData = wpd.appData.getPlotData();
			const fileManager = wpd.appData.getFileManager();
			const pageManager = wpd.appData.getPageManager();

			plotData.addAxes(tableAxis, wpd.appData.isMultipage());
			wpd.events.dispatch("wpd.axes.add", {
				axes: tableAxis,
			});

			fileManager.addAxesToCurrentFile([tableAxis]);
			if (wpd.appData.isMultipage()) {
				pageManager.addAxesToCurrentPage([tableAxis]);
			}

			for (let i = 0; i < calibration.cols.num; i++) {
				wpd.alignAxes.addDataset(i, tableAxis);
			}
			wpd.tree.refreshPreservingSelection();
		}

		wpd.alignAxes.updateTagGroupCheckboxes(metadata, false);

		const selectedAxis = wpd.tree.getActiveAxes();
		if (!selectedAxis) {
			wpd.tree.editAxis(tableAxis.name);
		}

		wpdDocument
			.getElementById("table-add-button")
			.classList.remove("pressed-button");

		// trigger table axis rows and column alignment tool
		wpd.graphicsWidget.setTool(
			new wpd.AlignmentRowsAndColumnsTool(calibration),
		);

		// display table axis rows and columns sidebar
		wpd.sidebar.show(ids.rcSidebar);
	};

	/**
	 * Sets the types of headers present.
	 */
	wpd.alignAxes.updateHeaderTypes = (headerlessInput) => {
		// update the local calibration data
		switch (headerlessInput.value) {
			case "both":
				calibration.rows.headerless = false;
				calibration.cols.headerless = false;
				break;
			case "rows":
				calibration.rows.headerless = false;
				calibration.cols.headerless = true;
				break;
			case "cols":
				calibration.rows.headerless = true;
				calibration.cols.headerless = false;
				break;
		}

		// get existing axis metadata
		const tableAxis = wpd.tree.getActiveAxes();
		const metadata = tableAxis.getMetadata();

		// update axis metadata calibration with local calibration
		metadata.calibration.rows.headerless = calibration.rows.headerless;
		metadata.calibration.cols.headerless = calibration.cols.headerless;

		// store data in the axis metadata field
		tableAxis.setMetadata(metadata);

		wpd.alignAxes.updateHeaderTypeOptions(calibration);

		// force redraw
		wpd.graphicsWidget.forceHandlerRepaint();
	};

	/**
	 * Syncs the edit sidebar header type select with a table calibration: its
	 * value, and which header types the table can still switch to.
	 */
	wpd.alignAxes.updateHeaderTypeOptions = (tableCalibration) => {
		const headerTypeInput = wpdDocument.getElementById(ids.editHeaderlessInput);
		const allowed = allowedHeaderTypes(tableCalibration);

		headerTypeInput.value = headerTypeValue(tableCalibration);
		Array.from(headerTypeInput.options).forEach((option) => {
			option.disabled = !allowed[option.value];
		});
	};

	/**
	 * Sets the table schema for pre-filling cells with used tags.
	 */
	wpd.alignAxes.updateTableSchema = (schemaInput) => {
		// update the local calibration data
		calibration.schema = schemaInput.value;

		// get existing axis metadata
		const tableAxis = wpd.tree.getActiveAxes();
		const metadata = tableAxis.getMetadata();

		// update axis metadata calibration with local calibration
		metadata.calibration.schema = calibration.schema;

		// store data in the axis metadata field
		tableAxis.setMetadata(metadata);
	};

	wpd.alignAxes.updateTableTagGroup = (tagGroupInput) => {
		const existingAxis = wpd.tree.getActiveAxes();

		// create an image axis to store table data under the hood
		const tableAxis = existingAxis ? existingAxis : new wpd.ImageAxes();

		// update the local calibration data
		calibration.cellTagGroup = {
			name: tagGroupInput.options[tagGroupInput.selectedIndex].text,
			uuid: tagGroupInput.options[tagGroupInput.selectedIndex].value,
		};

		// get existing axis metadata
		const metadata = tableAxis.getMetadata();

		// always set table to true and set calibration data
		metadata.table = true;
		metadata.calibration = calibration;

		// store data in the axis metadata field
		tableAxis.setMetadata(metadata);
	};

	/**
	 * Displays the details calibration sidebar.
	 */
	wpd.alignAxes.displayDetailsSidebar = (metadata) => {
		// load axis calibration into local calibration
		_.assign(calibration, metadata.calibration);

		wpd.graphicsWidget.setTool(new wpd.TableHeaderTool(calibration));

		let schemaValue;
		switch (calibration.schema) {
			case "cols":
				schemaValue = "cols";
				break;
			case "rows":
				schemaValue = "rows";
				break;
			default:
				schemaValue = "both";
		}

		const calibrationComplete = calibration.calibrationComplete;
		const schemaInputId = calibrationComplete
			? ids.editSchemaInput
			: ids.schemaInput;

		wpdDocument.getElementById(schemaInputId).value = schemaValue;

		if (calibrationComplete) {
			wpd.alignAxes.updateHeaderTypeOptions(calibration);
		} else {
			wpdDocument.getElementById(ids.headerlessInput).value =
				headerTypeValue(calibration);
		}

		wpd.alignAxes.updateTagGroupCheckboxes(metadata, calibrationComplete);

		// display table axis header sidebar
		if (calibration.calibrationComplete) {
			wpd.sidebar.show(ids.tableEditSidebar);
		} else {
			wpd.sidebar.show(ids.detailsSidebar);
		}
	};

	wpd.alignAxes.updateTagGroupCheckboxes = (metadata, isEdit = true) => {
		const checkboxClass = isEdit
			? ".table-calibration-edit-tag-group-checkbox"
			: ".table-calibration-tag-group-checkbox";
		const tagGroupCheckboxes = wpdDocument.querySelectorAll(checkboxClass);

		const rowHeaders = metadata.calibration.rows.headers;
		const colHeaders = metadata.calibration.cols.headers;
		const cellTagGroupUuids = _.uniq(
			[...rowHeaders, ...colHeaders]
				.flatMap((header) => {
					return header
						? Object.keys(header)
								.filter((key) => {
									return !isNaN(key);
								})
								.map((key) => {
									return header[key].tagGroup?.uuid;
								})
						: undefined;
				})
				.filter((uuid) => uuid !== undefined),
		);

		const hasHeaderData =
			!_.isEmpty([...rowHeaders, ...colHeaders]) &&
			[...rowHeaders, ...colHeaders].every((header) => !!header);

		const tagGroups =
			calibration.cellTagGroups?.map((group) => group.uuid) ?? [];

		tagGroupCheckboxes.forEach((checkbox) => {
			checkbox.checked = tagGroups.includes(checkbox.value);
			checkbox.disabled =
				cellTagGroupUuids.includes(checkbox.value) ||
				(calibration.cellTagGroups?.length === 1 &&
					calibration.cellTagGroups[0].uuid === checkbox.value &&
					hasHeaderData);
		});
	};

	wpd.alignAxes.backfillTableTagGroups = (axis) => {
		const axisMetadata = axis.getMetadata();
		if (
			axisMetadata.calibration?.cellTagGroup &&
			!axisMetadata.calibration?.cellTagGroups
		) {
			calibration.cellTagGroups = [axisMetadata.calibration?.cellTagGroup];
			calibration.cellTagGroup = axisMetadata.calibration?.cellTagGroup;
			axisMetadata.calibration.cellTagGroups = calibration.cellTagGroups;
			axisMetadata.calibration.cellTagGroup = calibration.cellTagGroup;

			const rowHeaders = axisMetadata.calibration.rows.headers;
			const colHeaders = axisMetadata.calibration.cols.headers;
			[...rowHeaders, ...colHeaders].forEach((header) => {
				Object.keys(header)
					.filter((key) => !isNaN(key))
					.forEach((key) => {
						const value = header[key];
						if (!value.tagGroup) {
							value.tagGroup = calibration.cellTagGroup;
						}
						header[key] = value;
					});
			});

			calibration.rows.headers = rowHeaders;
			calibration.cols.headers = colHeaders;

			axis.setMetadata(axisMetadata);

			const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);
			datasets.forEach((ds) => {
				const pixels = ds.getAllPixels();
				pixels.forEach((px, i) => {
					Object.keys(px.metadata)
						.filter((key) => !isNaN(key))
						.forEach((key) => {
							const value = px.metadata[key];
							if (!value.tagGroup) {
								value.tagGroup = calibration.cellTagGroup;
							}
						});
					ds.setMetadataAt(i, px.metadata);
				});
			});
		}
	};

	wpd.alignAxes.handleTagGroupCheckboxChange = (checkbox) => {
		const axis = wpd.tree.getActiveAxes();
		if (axis) {
			const metadata = axis.getMetadata();
			if (!calibration.cellTagGroups) {
				calibration.cellTagGroups = [];
			}

			const wasEmpty = _.isEmpty(calibration.cellTagGroups);
			calibration.cellTagGroups = checkbox.checked
				? [
						...calibration.cellTagGroups,
						{
							name: checkbox.closest("label").textContent.trim(),
							uuid: checkbox.value,
						},
				  ]
				: calibration.cellTagGroups.filter(
						(group) => group.uuid !== checkbox.value,
				  );

			const isEmpty = wpd.tableAxis.areTableHeadersEmpty();
			if (wasEmpty && !isEmpty) {
				calibration.cellTagGroup = calibration.cellTagGroups[0];
			} else if (!wasEmpty && isEmpty) {
				calibration.cellTagGroup = null;
			}

			metadata.calibration = calibration;
			axis.setMetadata(metadata);

			if (wasEmpty || isEmpty) {
				wpd.tree.refreshPreservingSelection();
			}
		}
	};

	wpd.alignAxes.addDataset = (colIndex, tableAxis) => {
		const plotData = wpd.appData.getPlotData();
		const fileManager = wpd.appData.getFileManager();
		const pageManager = wpd.appData.getPageManager();

		// make a new uuid
		const uuid = uuidv4();

		// get column header value for use as dataset name
		const name = "Column";

		// create a new dataset
		const dataset = new wpd.Dataset();

		// set dataset name to column and skip over existing column names
		dataset.name = `${name}_${uuid}`;

		// add dataset metadata
		dataset.setMetadata({
			table: true,
			colIndex: colIndex,
			name: name,
			uuid: uuid,
		});

		// add dataset to plot data, current file, and current page
		plotData.addDataset(dataset);
		fileManager.addDatasetsToCurrentFile([dataset]);

		if (wpd.appData.isMultipage()) {
			pageManager.addDatasetsToCurrentPage([dataset]);
		}

		// associate newly created dataset with table axis
		plotData.setAxesForDataset(dataset, tableAxis);

		// dispatch dataset add event
		wpd.events.dispatch("wpd.dataset.add", {
			dataset: dataset,
		});

		return dataset;
	};

	/**
	 * Creates or updates table axis after calibration.
	 */
	wpd.alignAxes.completeTableAxisCalibration = () => {
		if (calibration.rows.num <= 0 || calibration.cols.num <= 0) {
			wpd.tableAxis.showInvalidTableHeaderCountDialog();
			return;
		}

		// update the local calibration data
		const tagGroupCheckboxes = wpdDocument.querySelectorAll(
			".table-calibration-tag-group-checkbox",
		);

		calibration.cellTagGroups = Array.from(tagGroupCheckboxes).reduce(
			(acc, checkbox) => {
				if (checkbox.checked) {
					acc.push({
						name: checkbox.closest("label").textContent.trim(),
						uuid: checkbox.value,
					});
				}
				return acc;
			},
			[],
		);

		if (_.isEmpty(calibration.cellTagGroups)) {
			wpd.tableAxis.showTableMissingTagGroupDialog();
			return;
		}

		// For some semblance of backwards compatibility
		calibration.cellTagGroup = calibration.cellTagGroups[0];

		const existingAxis = wpd.tree.getActiveAxes();
		const edit = existingAxis ? true : false;

		// create an image axis to store table data under the hood
		const tableAxis = edit ? existingAxis : new wpd.ImageAxes();

		// set the axis name to table
		if (!edit) {
			tableAxis.name = wpd.custom.makeAxisName("Table");
		}

		// set active axis
		wpd.tree.setActiveAxis(tableAxis);

		// get existing axis metadata
		const metadata = tableAxis.getMetadata();

		// sync column and row headers data
		syncHeaders(metadata.calibration, calibration);

		// always set table to true and set calibration data
		metadata.table = true;
		calibration.wizardStep = "definePrefillSettings";
		metadata.calibration = calibration;

		// store data in the axis metadata field
		tableAxis.setMetadata(metadata);

		const plotData = wpd.appData.getPlotData();
		const fileManager = wpd.appData.getFileManager();
		const pageManager = wpd.appData.getPageManager();

		// add the new axis to plot data
		if (!edit) {
			plotData.addAxes(tableAxis, wpd.appData.isMultipage());

			// dispatch axis add event
			wpd.events.dispatch("wpd.axes.add", {
				axes: tableAxis,
			});

			// add axis to current file
			fileManager.addAxesToCurrentFile([tableAxis]);

			// add axis to current page if necessary, and filter axes and datasets to the current page
			if (wpd.appData.isMultipage()) {
				pageManager.addAxesToCurrentPage([tableAxis]);
			}

			// create a dataset for each column
			for (let i = 0; i < calibration.cols.num; i++) {
				wpd.alignAxes.addDataset(i, tableAxis);
			}

			// update the left sidebar
			wpd.tree.refreshPreservingSelection();

			// select the newly created axis
			wpd.tree.editAxis(tableAxis.name);

			const cancelButton = wpdDocument.getElementsByClassName(
				"table-calibration-sidebar-cancel-button",
			)[0];
			cancelButton.classList.remove("hidden");

			// reset add new table button
			wpdDocument
				.getElementById("table-add-button")
				.classList.remove("pressed-button");
		} else {
			// get datasets within current file
			let datasets = fileManager.filterToCurrentFileDatasets(
				plotData.getDatasets(),
			);

			if (wpd.appData.isMultipage()) {
				// and within current page if multipage
				datasets = pageManager.filterToCurrentPageDatasets(datasets);
			}

			// filter down to table datasets in the table axis
			datasets = datasets.filter((dataset) => {
				return (
					dataset.getMetadata().table &&
					tableAxis === plotData.getAxesForDataset(dataset)
				);
			});

			// add or remove datasets based on new column number
			if (datasets.length !== calibration.cols.num) {
				if (datasets.length > calibration.cols.num) {
					// remove extra columns from the end
					const deletes = datasets.splice(calibration.cols.num);
					deletes.forEach((dataset) => plotData.deleteDataset(dataset));
				} else if (datasets.length < calibration.cols.num) {
					// add extra columns at the end
					for (let i = datasets.length; i < calibration.cols.num; i++) {
						datasets.push(wpd.alignAxes.addDataset(i, tableAxis));
					}
				}
			}

			// re-index the columns
			if (dataEntryVM.pointGroupOverhaul) {
				datasets.forEach((dataset, i) => {
					dataset.setMetadata(
						_.assign(dataset.getMetadata(), {
							table: true,
							colIndex: i,
						}),
					);
				});
			} else {
				datasets.forEach((dataset, i) => {
					dataset.setMetadata({
						table: true,
						colIndex: i,
					});
				});
			}

			// update the left sidebar
			wpd.tree.refreshPreservingSelection();

			const selectedAxis = wpd.tree.getActiveAxes();
			if (!selectedAxis || selectedAxis.name !== tableAxis.name) {
				wpd.tree.editAxis(tableAxis.name);
			}
		}
	};

	wpd.alignAxes.resizeTable = () => {
		wpd.sidebar.show("table-axis-resize-sidebar");
		const axis = wpd.tree.getActiveAxes();
		wpd.graphicsWidget.setTool(new wpd.EditAlignmentAreaTool(calibration));
	};

	wpd.alignAxes.completeResizeTable = () => {
		wpd.sidebar.clear();
		wpd.graphicsWidget.removeTool();

		const axis = wpd.tree.getActiveAxes();
		const metadata = axis.getMetadata();
		metadata.calibration = calibration;
		axis.setMetadata(metadata);

		wpd.alignAxes.displayDetailsSidebar(axis.getMetadata());
	};

	wpd.alignAxes.adjustRowsAndColumns = () => {
		wpd.sidebar.show("table-axis-adjust-rows-and-columns");

		wpd.graphicsWidget.setTool(
			new wpd.AlignmentRowsAndColumnsTool(calibration),
		);
	};

	wpd.alignAxes.addRow = () => {
		wpd.graphicsWidget.setTool(
			new wpd.AddRowOrColumnDividerTool(calibration, true),
		);
	};

	wpd.alignAxes.addColumn = () => {
		wpd.graphicsWidget.setTool(
			new wpd.AddRowOrColumnDividerTool(calibration, false),
		);
	};

	wpd.alignAxes.removeRow = () => {
		wpd.graphicsWidget.setTool(
			new wpd.RemoveRowOrColumnTool(calibration, true),
		);
	};

	wpd.alignAxes.removeColumn = () => {
		wpd.graphicsWidget.setTool(
			new wpd.RemoveRowOrColumnTool(calibration, false),
		);
	};

	wpd.alignAxes.completeAdjustRowsAndColumns = () => {
		wpd.sidebar.clear();
		wpd.graphicsWidget.removeTool();

		const axis = wpd.tree.getActiveAxes();
		const metadata = axis.getMetadata();
		metadata.calibration = calibration;
		axis.setMetadata(metadata);

		wpd.alignAxes.displayDetailsSidebar(axis.getMetadata());
	};

	wpd.alignAxes.closeTableEdit = () => {
		const isEmpty = wpd.tableAxis.areTableHeadersEmpty();
		const isValid = wpd.tableAxis.validateTableHeaders();
		const hasEmptyHeaders = wpd.tableAxis.hasEmptyTableHeaders();

		if (isEmpty) {
			wpd.tableAxis.showEmptyTableHeadersDialog();
		} else if (!isValid) {
			wpd.tableAxis.showInvalidTableHeadersDialog();
		} else if (hasEmptyHeaders) {
			wpd.tableAxis.showHasEmptyTableHeadersDialog();
		} else {
			wpd.graphicsWidget.removeTool();
			wpd.sidebar.clear();
			wpd.tree.refreshPreservingSelection();
		}
	};
}

export { apply, allowedHeaderTypes, headerTypeValue };
